import { ComponentType } from "react";
import { UseFormSetValue } from "react-hook-form";
import { z } from "zod";
import {
  calculateJacobBear,
  calculateKrijgsmanLoboFerreira,
  calculateTransientFlowFixedRadius,
  calculateUniformFlow,
  calculateWyssling,
  getCalculationByInputId,
} from "../api/methods";
import JacobBearExtraInputsFormInputs from "../components/Methods/JacobBearExtraInputsForm";
import JacobBearResults from "../components/Methods/JacobBearResults";
import KrijgsmanLoboFerreiraExtraInputsForm from "../components/Methods/KrijgsmanLoboFerreiraExtraInputsForm";
import KrijgsmanLoboFerreiraResults from "../components/Methods/KrijgsmanLoboFerreiraResults";
import TransientFlowFixedRadiusResults from "../components/Methods/TransientFlowFixedRadiusResults";
import UniformFlowResults from "../components/Methods/UniformFlowResults";
import WysslingExtraInputsForm from "../components/Methods/WysslingExtraInputsForm";
import WysslingResults from "../components/Methods/WysslingResults";
import {
  JacobBearResult,
  KrijgsmanLoboFerreiraResult,
  Method,
  TransientFlowFixedRadiusResult,
  UniformFlowResult,
  WysslingResult
} from "../types/interfaces/methods";
import { JacobBearRequestSchema, KrijgsmanLoboFerreiraRequestSchema, WysslingRequestSchema } from "../types/model/calculation";
import { MethodsInput } from "../types/model/methodsInput";

export interface MethodConfig<TInputs, TResult = any> {
  method: Method;
  labelKey: string;
  inputs?: (keyof MethodsInput)[];

  extraInputsSchema?: z.ZodSchema<TInputs>;
  ExtraInputsComponent?: ComponentType<{ canEdit: boolean }>;
  populateExtraInputs?: (res: TResult, setValue: any) => void;
  resetExtraInputs?: (setValue: UseFormSetValue<any>) => void;

  calculate: (captureId: number, inputId: number, payload?: TInputs) => Promise<void>;
  getResults: (captureId: number, inputId: number) => Promise<TResult[]>;

  ResultsComponent: React.ComponentType<{ calculations: TResult[]; captureId?: number }>;
}

export const FLOW_TEST_METHODS_CONFIG: MethodConfig<any>[] = [
  {
    method: Method.TRANSIENT_FLOW_FIXED_RADIUS,
    labelKey: "calculations.methods.tabs.transient-flow-fixed-radius",

    inputs: [
      "saturatedThicknessTop",
      "saturatedThicknessBase",
      "exploitationFlow",  
      "hydraulicConductivity",
      "hydraulicGradient",
      "effectivePorosity",
      "recharge",
    ],

    calculate: (captureId, inputId) =>
      calculateTransientFlowFixedRadius(captureId, inputId),
    getResults: async (captureId: number, inputId: number) => {
      const raw = await getCalculationByInputId(
        captureId,
        inputId,
        Method.TRANSIENT_FLOW_FIXED_RADIUS
      );

      return raw.map(r => ({
        id: r.id,
        inputId: r.inputId,
        results: r.results,
      })) as TransientFlowFixedRadiusResult[];
    },

    ResultsComponent: TransientFlowFixedRadiusResults,
  },
  {
    method: Method.WYSSLING,
    labelKey: "calculations.methods.tabs.wyssling",
    inputs: [
      "saturatedThicknessTop",
      "saturatedThicknessBase",
      "exploitationFlow",
      "hydraulicConductivity",
      "hydraulicGradient",
      "effectivePorosity",
    ],
    extraInputsSchema: WysslingRequestSchema,
    ExtraInputsComponent: WysslingExtraInputsForm,
    calculate: (captureId, inputId, payload) =>
      calculateWyssling(captureId, inputId, payload),
    populateExtraInputs: (res, setValue) => {
      const userGradients = res.inputParameters.iW ?? [0];
      setValue("userGradients", userGradients);
    },
    resetExtraInputs: (setValue) => {
      setValue("userGradients", [0]);
    },
    getResults: async (captureId: number, inputId: number) => {
      const raw = await getCalculationByInputId(
        captureId,
        inputId,
        Method.WYSSLING
      );

      return raw.map((r) => ({
        id: r.id,
        inputId: r.inputId,
        results: r.results,
        inputParameters: r.inputParameters,
      })) as WysslingResult[];
    },
    ResultsComponent: WysslingResults,
  },
  {
    method: Method.UNIFORM_FLOW,
    labelKey: "calculations.methods.tabs.uniform-flow",

    inputs: [
      "saturatedThicknessTop",
      "saturatedThicknessBase",
      "exploitationFlow",
      "hydraulicConductivity",
      "hydraulicGradient",
    ],

    calculate: (captureId, inputId) => calculateUniformFlow(captureId, inputId),
    getResults: async (captureId: number, inputId: number) => {
      const raw = await getCalculationByInputId(
        captureId,
        inputId,
        Method.UNIFORM_FLOW
      );

      return raw.map((r) => ({
        id: r.id,
        inputId: r.inputId,
        results: {
          fluxoUnifRecarga: {
            fcuXL: r.results.fcuXL,
            fcuYL1: r.results.fcuYL1,
            fcuYL2: r.results.fcuYL2,
          },
        },
      })) as UniformFlowResult[];
    },

    ResultsComponent: UniformFlowResults,
  },
  {
    method: Method.KRIJGSMAN_LOBO_FERREIRA,
    labelKey: "calculations.methods.tabs.krijgsman-lobo-ferreira",
    inputs: [
      "saturatedThicknessTop",
      "saturatedThicknessBase",
      "exploitationFlow",
      "hydraulicConductivity",
      "hydraulicGradient",
      "effectivePorosity",
    ],
    extraInputsSchema: KrijgsmanLoboFerreiraRequestSchema,
    ExtraInputsComponent: KrijgsmanLoboFerreiraExtraInputsForm,
    populateExtraInputs: (res, setValue) => {
      const userGradients = res.inputParameters.iKLF ?? [0];
      setValue("userGradients", userGradients);
    },
    resetExtraInputs: (setValue) => {
      setValue("userGradients", [0]);
    },
    calculate: (captureId, inputId, payload) =>
      calculateKrijgsmanLoboFerreira(captureId, inputId, payload),
    getResults: async (captureId: number, inputId: number) => {
      const raw = await getCalculationByInputId(
        captureId,
        inputId,
        Method.KRIJGSMAN_LOBO_FERREIRA
      );

      return raw.map((r) => ({
        id: r.id,
        inputId: r.inputId,
        results: r.results,
        inputParameters: r.inputParameters,
      })) as KrijgsmanLoboFerreiraResult[];
    },
    ResultsComponent: KrijgsmanLoboFerreiraResults,
  },
  {
    method: Method.JACOB_BEAR,
    labelKey: "calculations.methods.tabs.jacob-bear",
    inputs: [
      "saturatedThicknessTop",
      "saturatedThicknessBase",
      "exploitationFlow",
      "transmissivity",
      "effectivePorosity",
    ],
    extraInputsSchema: JacobBearRequestSchema,
    ExtraInputsComponent: JacobBearExtraInputsFormInputs,
    populateExtraInputs: (res, setValue) => {
      const parameters = res.inputParameters?.parameters;

      setValue(
        "parameters",
        Array.isArray(parameters) && parameters.length > 0
          ? parameters
          : [{ iJB: undefined, tJB: undefined, gradi: undefined }]
      );
    },
    resetExtraInputs: (setValue) => {
      setValue("parameters", [
        { iJB: undefined, tJB: undefined, gradi: undefined }
      ]);
    },
    calculate: (captureId, inputId, payload) =>
      calculateJacobBear(captureId, inputId, payload),
    getResults: async (captureId: number, inputId: number) => {
      const raw = await getCalculationByInputId(
        captureId,
        inputId,
        Method.JACOB_BEAR
    );

      return raw.map((r) => ({
        id: r.id,
        inputId: r.inputId,
        results: {
          calculations: r.results.calculations,
        },
        inputParameters: r.inputParameters,
      })) as JacobBearResult[];
    },
    ResultsComponent: JacobBearResults,
  },
];
